/**
 * Email.jsx — Outlook-style mailbox (Vague 113 L8 → refonte Vague 114)
 *
 * 3-column layout:
 *   ├─ Sidebar (folders + custom labels)
 *   ├─ Threads list (selected folder)
 *   └─ Thread viewer (selected thread)
 *
 * Vague 114 — page slimmed down: rendering blocks extracted into
 * components/email/ (ThreadRow, ThreadList, BulkBar, ViewerHeader,
 * MessageList, LinkCrmModal, ResizeHandle). This file keeps the state
 * machine + API calls only.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Mail, ChevronRight, Filter } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import EmailComposeDialog from '../components/EmailComposeDialog';
import EmailSidebar from '../components/email/EmailSidebar';
import EmailShortcutsModal from '../components/email/EmailShortcutsModal';
import EmailSavedSearchesModal from '../components/email/EmailSavedSearchesModal';
import EmailToolbar from '../components/email/EmailToolbar';
import EmailQuickFilters from '../components/email/EmailQuickFilters';
import EmailThreadCollab from '../components/email/EmailThreadCollab';
import EmailSnoozePopover from '../components/email/EmailSnoozePopover';
import EmailAdvancedFiltersPopover from '../components/email/EmailAdvancedFiltersPopover';
import EmailResizeHandle from '../components/email/EmailResizeHandle';
import EmailThreadList from '../components/email/EmailThreadList';
import EmailBulkBar from '../components/email/EmailBulkBar';
import EmailViewerHeader from '../components/email/EmailViewerHeader';
import EmailMessageList from '../components/email/EmailMessageList';
import EmailLinkCrmModal from '../components/email/EmailLinkCrmModal';
import { accountHeaderValue, formatAccountLabel } from '../components/email/emailUtils';

export default function Email() {
  const [status, setStatus] = useState(null);
  const [folder, setFolder] = useState('INBOX');
  const [labels, setLabels] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Wave 1b — compose state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('new'); // 'new' | 'reply' | 'reply_all' | 'forward' | 'draft'
  const [composeDraftId, setComposeDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  // Wave 1f — multi-account selector
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  // Wave 1s Phase A — H1/H4/H3/F3/I3
  const [quickFilter, setQuickFilter] = useState('all');
  const [focusMode, setFocusMode] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState(() => new Set());
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchModalOpen, setSavedSearchModalOpen] = useState(false);
  const [newSavedSearchName, setNewSavedSearchName] = useState('');
  // Wave 1t — manual sync button + last sync hint
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  // Wave 1u3 Phase D / F4 — Snooze popover
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // Wave 1u3 Phase D / I1 — Advanced filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [advFilters, setAdvFilters] = useState({ tags: [], status: '', assignee: '', since: '', until: '' });
  const [team, setTeam] = useState([]);
  // Vague 113 / Wave 1u4 — resizable column widths persisted per user.
  const [sidebarWidth, setSidebarWidth] = useState(224);
  const [threadsWidth, setThreadsWidth] = useState(380);
  const [resizing, setResizing] = useState(null); // 'sidebar' | 'threads' | null
  // Wave 1u4 — folder unread counts for sidebar badges
  const [folderCounts, setFolderCounts] = useState({});
  // Wave 1u6 — collapse the "Mes libellés" section on user request.
  const [labelsCollapsed, setLabelsCollapsed] = useState(false);
  // Wave 1u7 — pagination state (provider continuation cursor).
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load saved column widths once
  useEffect(() => {
    api.get('/user/email-layout')
      .then(({ data }) => {
        if (data?.sidebar_width) setSidebarWidth(Math.max(160, Math.min(360, data.sidebar_width)));
        if (data?.threads_width) setThreadsWidth(Math.max(280, Math.min(640, data.threads_width)));
        if (typeof data?.labels_collapsed === 'boolean') setLabelsCollapsed(data.labels_collapsed);
      })
      .catch(() => {});
  }, []);

  const persistWidths = React.useCallback((sw, tw) => {
    api.put('/user/email-layout', { sidebar_width: sw, threads_width: tw }).catch(() => {});
  }, []);

  const toggleLabels = (collapsed) => {
    setLabelsCollapsed(collapsed);
    api.put('/user/email-layout', { labels_collapsed: collapsed }).catch(() => {});
  };

  const resetWidths = () => {
    setSidebarWidth(224);
    setThreadsWidth(380);
    persistWidths(224, 380);
    toast({ title: 'Largeur des colonnes réinitialisée' });
  };

  const loadFolderCounts = React.useCallback(() => {
    api.get('/mailbox/folder-counts')
      .then(({ data }) => setFolderCounts(data?.counts || {}))
      .catch(() => {});
  }, []);

  // Global mouse handlers for the drag-to-resize dividers.
  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      if (resizing === 'sidebar') {
        const w = Math.max(160, Math.min(360, e.clientX));
        setSidebarWidth(w);
      } else if (resizing === 'threads') {
        const w = Math.max(280, Math.min(640, e.clientX - sidebarWidth));
        setThreadsWidth(w);
      }
    };
    const onUp = () => {
      setResizing(null);
      persistWidths(sidebarWidth, threadsWidth);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing, sidebarWidth, threadsWidth, persistWidths]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/mailbox/status');
      setStatus(data);
      const accs = data?.accounts || [];
      setAccounts(accs);
      if (accs.length && !selectedAccount) {
        const first = accs.find((a) => a.can_read) || accs[0];
        setSelectedAccount(first?.provider + ':' + (first?.shared_config_id || first?.email || 'default'));
      }
    } catch (err) {
      setStatus({ has_inbox: false, accounts: [] });
      setAccounts([]);
    }
  };

  const loadLabels = async () => {
    try {
      const { data } = await api.get('/mailbox/labels');
      setLabels(data?.labels || []);
    } catch { /* labels are optional */ }
  };

  // Wave 1u3 — keep freshly-toggled unread state alive during Graph propagation.
  const recentReadOverridesRef = React.useRef(new Map());
  const autoMarkReadAbortRef = React.useRef(null);
  const markRecentRead = (threadId, value) => {
    recentReadOverridesRef.current.set(threadId, { unread: value, ts: Date.now() });
    setTimeout(() => {
      const entry = recentReadOverridesRef.current.get(threadId);
      if (entry && Date.now() - entry.ts >= 12000) {
        recentReadOverridesRef.current.delete(threadId);
      }
    }, 12000);
  };
  const applyRecentReadOverrides = (rawThreads) => {
    const now = Date.now();
    for (const [tid, entry] of Array.from(recentReadOverridesRef.current.entries())) {
      if (now - entry.ts > 12000) recentReadOverridesRef.current.delete(tid);
    }
    if (recentReadOverridesRef.current.size === 0) return rawThreads;
    return rawThreads.map((t) => {
      const override = recentReadOverridesRef.current.get(t.thread_id);
      return override ? { ...t, unread: override.unread } : t;
    });
  };

  const loadThreads = async () => {
    setLoadingList(true);
    setThread(null);
    setSelectedThreadId(null);
    setNextPageToken(null);
    try {
      if (folder === 'DRAFT') {
        const { data } = await api.get('/mailbox/drafts');
        setDrafts(data?.drafts || []);
        setThreads([]);
      } else {
        const params = { folder, max_results: 100 };
        if (search) params.q = search;
        if (advFilters.tags && advFilters.tags.length > 0) params.tags = advFilters.tags.join(',');
        if (advFilters.status) params.status = advFilters.status;
        if (advFilters.assignee) params.assignee = advFilters.assignee;
        if (advFilters.since) params.since = advFilters.since;
        if (advFilters.until) params.until = advFilters.until;

        // Wave 1u5 — "Toutes les boîtes" combined view.
        if (selectedAccount === '__all__' && accounts.length > 0) {
          const calls = accounts
            .filter((a) => a.can_read !== false)
            .map(async (a) => {
              const header = accountHeaderValue(a);
              try {
                const { data } = await api.get('/mailbox/threads', {
                  params,
                  headers: header ? { 'X-Mailbox-Account': header } : {},
                });
                return (data?.threads || []).map((t) => ({
                  ...t,
                  _account_label: formatAccountLabel(a),
                  _account_email: a.email || '',
                }));
              } catch { return []; }
            });
          const results = await Promise.all(calls);
          const merged = results.flat();
          merged.sort((x, y) => {
            const dx = new Date(x.last_message_at || x.date || 0).getTime();
            const dy = new Date(y.last_message_at || y.date || 0).getTime();
            return dy - dx;
          });
          setThreads(applyRecentReadOverrides(merged));
          setDrafts([]);
        } else {
          const { data } = await api.get('/mailbox/threads', { params });
          setThreads(applyRecentReadOverrides(data?.threads || []));
          setNextPageToken(data?.next_page_token || null);
          setDrafts([]);
        }
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de charger la boîte de réception' });
      setThreads([]);
      setDrafts([]);
    } finally { setLoadingList(false); }
  };

  // Wave 1u7 — Fetch the next page of threads.
  const loadMoreThreads = async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { folder, max_results: 100, page_token: nextPageToken };
      if (search) params.q = search;
      if (advFilters.tags?.length > 0) params.tags = advFilters.tags.join(',');
      if (advFilters.status) params.status = advFilters.status;
      if (advFilters.assignee) params.assignee = advFilters.assignee;
      if (advFilters.since) params.since = advFilters.since;
      if (advFilters.until) params.until = advFilters.until;
      const { data } = await api.get('/mailbox/threads', { params });
      setThreads((prev) => [...prev, ...applyRecentReadOverrides(data?.threads || [])]);
      setNextPageToken(data?.next_page_token || null);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de charger plus' });
    } finally { setLoadingMore(false); }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => {
    if (status?.has_inbox) {
      loadLabels();
      loadSavedSearches();
      loadThreads();
      loadFolderCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, folder, search, advFilters]);

  // Wave 1u3 Phase D — team list (collab panel + filters popover)
  useEffect(() => {
    if (!status?.has_inbox) return;
    api.get('/mailbox/team')
      .then(({ data }) => setTeam(Array.isArray(data?.members) ? data.members : []))
      .catch(() => setTeam([]));
  }, [status?.has_inbox]);

  // Wave 1s Phase A — saved searches
  const loadSavedSearches = async () => {
    try {
      const { data } = await api.get('/mailbox/saved-searches');
      setSavedSearches(data?.searches || []);
    } catch { /* optional */ }
  };
  const saveCurrentSearch = async () => {
    const q = (search || '').trim();
    if (!q) { toast({ title: 'Saisissez d\'abord une recherche' }); return; }
    if (!newSavedSearchName.trim()) { toast({ title: 'Nom requis' }); return; }
    try {
      await api.post('/mailbox/saved-searches', { name: newSavedSearchName.trim(), query: q, folder });
      setNewSavedSearchName('');
      setSavedSearchModalOpen(false);
      loadSavedSearches();
      toast({ title: 'Recherche enregistrée' });
    } catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
  };
  const deleteSavedSearch = async (id) => {
    try { await api.delete(`/mailbox/saved-searches/${id}`); loadSavedSearches(); }
    catch { /* ignore */ }
  };
  const applySavedSearch = (s) => {
    setFolder(s.folder || 'INBOX');
    setSearch(s.query);
    setSearchInput(s.query);
    setSavedSearchModalOpen(false);
  };

  // Quick filter + focus mode (client-side) — memoized for list perf.
  const visibleThreads = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
    return threads.filter((t) => {
      if (focusMode && !t.unread && !t.crm_match) return false;
      switch (quickFilter) {
        case 'unread': return !!t.unread;
        case 'crm': return !!t.crm_match;
        case 'attachments': return !!t.has_attachments;
        case 'starred': return !!t.starred;
        case 'week': return (t.date_ms || 0) >= weekAgo;
        default: return true;
      }
    });
  }, [threads, focusMode, quickFilter]);

  const toggleThreadSelected = React.useCallback((tid) => {
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }, []);
  const clearSelection = () => setSelectedThreadIds(new Set());

  const runBulk = async (action) => {
    if (selectedThreadIds.size === 0) return;
    const items = threads
      .filter((t) => selectedThreadIds.has(t.thread_id))
      .map((t) => ({ thread_id: t.thread_id, message_ids: t.message_ids || [t.thread_id] }));
    // Wave 1u7 — optimistic removal for destructive actions.
    const REMOVES_FROM_VIEW = new Set(['trash', 'archive', 'spam', 'unspam']);
    const prev = threads;
    if (REMOVES_FROM_VIEW.has(action)) {
      setThreads((p) => p.filter((t) => !selectedThreadIds.has(t.thread_id)));
    }
    try {
      const { data } = await api.post('/mailbox/threads/bulk', { action, items });
      const labelsMap = {
        'mark-read': 'marqué(s) comme lu(s)',
        'mark-unread': 'marqué(s) comme non lu(s)',
        'archive': 'archivé(s)',
        'trash': 'mis à la corbeille',
        'star': 'mis en suivi',
        'spam': 'déplacé(s) en spam',
        'unspam': 'sorti(s) du spam',
      };
      toast({ title: `${data.applied} message${data.applied > 1 ? 's' : ''} ${labelsMap[action] || 'mis à jour'}` });
      clearSelection();
      if (!REMOVES_FROM_VIEW.has(action)) loadThreads();
      loadFolderCounts();
    } catch (err) {
      if (REMOVES_FROM_VIEW.has(action)) setThreads(prev);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const toggleStarOnThread = React.useCallback(async (t) => {
    const willStar = !t.starred;
    try {
      await api.post(`/mailbox/threads/${t.thread_id}/${willStar ? 'star' : 'unstar'}`, {
        message_ids: t.message_ids || [t.thread_id],
      });
      setThreads((prev) => prev.map((x) => x.thread_id === t.thread_id ? { ...x, starred: willStar } : x));
    } catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' }); }
  }, []);

  // Wave 1t — Manual sync
  const refreshSyncStatus = async () => {
    try {
      const { data } = await api.get('/mailbox/sync-status');
      const newest = (data.backends || []).reduce((acc, b) => {
        const ts = b.last_run_at && new Date(b.last_run_at).getTime();
        return ts && (!acc || ts > acc) ? ts : acc;
      }, null);
      setLastSyncAt(newest ? new Date(newest).toISOString() : null);
    } catch { /* silent */ }
  };
  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post('/mailbox/sync-now');
      await refreshSyncStatus();
      await loadThreads();
      toast({ title: 'Synchronisé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec synchronisation' });
    } finally { setSyncing(false); }
  };
  useEffect(() => { refreshSyncStatus(); }, []);

  // Wave 1u3 — auto-refresh when the tab becomes visible again.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && status?.has_inbox) {
        loadThreads();
        refreshSyncStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.has_inbox, folder, search]);

  const lastSyncLabel = (() => {
    if (!lastSyncAt) return null;
    const diff = Date.now() - new Date(lastSyncAt).getTime();
    const min = Math.max(0, Math.floor(diff / 60000));
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    return `il y a ${h}h`;
  })();

  const openThread = React.useCallback(async (t) => {
    setSelectedThreadId(t.thread_id);
    setLoadingThread(true);
    // Wave 1u3 — optimistic read-state update immediately on click.
    if (t.unread) {
      markRecentRead(t.thread_id, false);
      setThreads((prev) => prev.map((x) => x.thread_id === t.thread_id ? { ...x, unread: false } : x));
      setFolderCounts((prev) => {
        const fc = { ...(prev || {}) };
        if (fc[folder]?.unread > 0) fc[folder] = { ...fc[folder], unread: fc[folder].unread - 1 };
        return fc;
      });
    }
    try {
      // Wave 1u5 — combined view: fetch from the thread's origin account.
      let cfg;
      if (selectedAccount === '__all__' && t._account_email) {
        const acct = accounts.find((a) => a.email === t._account_email);
        const hdr = accountHeaderValue(acct);
        if (hdr) cfg = { headers: { 'X-Mailbox-Account': hdr } };
      }
      const { data } = await api.get(`/mailbox/threads/${t.thread_id}`, cfg);
      setThread(data);
      // Auto mark-read (abortable — see toggleReadUnread race note).
      if (t.unread) {
        const unreadIds = (data?.messages || [])
          .filter((m) => (m.label_ids || []).includes('UNREAD'))
          .map((m) => m.message_id);
        if (unreadIds.length > 0) {
          try { autoMarkReadAbortRef.current?.abort(); } catch { /* noop */ }
          const ctl = new AbortController();
          autoMarkReadAbortRef.current = ctl;
          api.post(
            `/mailbox/threads/${t.thread_id}/mark-read`,
            { message_ids: unreadIds },
            { signal: ctl.signal },
          ).catch(() => { /* best-effort — next sync reconciles */ });
        }
      }
    } catch (err) {
      if (err?.response?.status === 424) {
        toast({
          title: 'Boîte email à reconnecter',
          description: 'Votre boîte Outlook/Gmail a expiré. Allez sur Profil → Boîte email et cliquez sur « Reconnecter ».',
        });
      } else {
        toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de charger ce message' });
      }
      setThread(null);
    } finally { setLoadingThread(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, selectedAccount, accounts]);

  const archive = async () => {
    if (!thread) return;
    try {
      await api.post(`/mailbox/threads/${thread.thread_id}/archive`, {
        message_ids: thread.messages.map((m) => m.message_id),
      });
      setThreads((prev) => prev.filter((x) => x.thread_id !== thread.thread_id));
      setThread(null);
      setSelectedThreadId(null);
      toast({ title: 'Archivé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  // Wave 1r — Toggle read/unread on the open thread (manual)
  const toggleReadUnread = async () => {
    if (!thread) return;
    const isUnread = threads.find((x) => x.thread_id === thread.thread_id)?.unread;
    const path = isUnread ? 'mark-read' : 'mark-unread';
    if (!isUnread) {
      try { autoMarkReadAbortRef.current?.abort(); } catch { /* noop */ }
      autoMarkReadAbortRef.current = null;
    }
    markRecentRead(thread.thread_id, !isUnread);
    setThreads((prev) => prev.map((x) => x.thread_id === thread.thread_id ? { ...x, unread: !isUnread } : x));
    try {
      await api.post(`/mailbox/threads/${thread.thread_id}/${path}`, {
        message_ids: thread.messages.map((m) => m.message_id),
      });
      toast({ title: isUnread ? 'Marqué comme lu' : 'Marqué comme non lu' });
    } catch (err) {
      recentReadOverridesRef.current.delete(thread.thread_id);
      setThreads((prev) => prev.map((x) => x.thread_id === thread.thread_id ? { ...x, unread: !!isUnread } : x));
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  // Wave 1u3 Phase D / F4 — Snooze actions
  const snoozeThread = async (isoUntil) => {
    if (!thread) return;
    try {
      await api.post(`/mailbox/threads/${thread.thread_id}/snooze`, { until: isoUntil });
      setThread((prev) => prev ? { ...prev, snoozed_until: isoUntil } : prev);
      if (folder !== 'SNOOZED') {
        setThreads((prev) => prev.filter((x) => x.thread_id !== thread.thread_id));
        setSelectedThreadId(null);
        setThread(null);
      }
      toast({ title: 'Message reporté', description: `Réapparaîtra le ${new Date(isoUntil).toLocaleString('fr-FR')}` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec du report' });
    }
  };

  const unsnoozeThread = async () => {
    if (!thread) return;
    try {
      await api.delete(`/mailbox/threads/${thread.thread_id}/snooze`);
      setThread((prev) => prev ? { ...prev, snoozed_until: null } : prev);
      if (folder === 'SNOOZED') {
        setThreads((prev) => prev.filter((x) => x.thread_id !== thread.thread_id));
        setSelectedThreadId(null);
        setThread(null);
      }
      toast({ title: 'Report annulé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  // Wave 1r — Manual attach to an entreprise
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkSuggestions, setLinkSuggestions] = useState({ prospects: [], learners: [], entreprises: [] });
  const [linkLoading, setLinkLoading] = useState(false);

  const fetchLinkSuggestions = async () => {
    if (!thread) return;
    setLinkLoading(true);
    try {
      const { data } = await api.get(`/mailbox/threads/${thread.thread_id}/crm-suggestions`, {
        params: { email: thread.counterparty_email || '' },
      });
      setLinkSuggestions(data);
    } catch (err) { console.warn('[link suggestions]', err); }
    finally { setLinkLoading(false); }
  };

  const linkToEntreprise = async (entId, updateContactEmail = false) => {
    if (!thread) return;
    try {
      const lastMsg = (thread.messages && thread.messages[thread.messages.length - 1]) || null;
      const snippetSrc = lastMsg?.snippet || lastMsg?.body_text || thread.snippet || '';
      const payload = {
        entreprise_id: entId,
        counterparty_email: thread.counterparty_email || null,
        update_contact_email: updateContactEmail,
        subject: thread.subject || '',
        snippet: String(snippetSrc).slice(0, 300),
        counterparty_name: thread.counterparty_name || null,
        last_message_at: lastMsg?.date || thread.last_message_at || null,
      };
      const { data } = await api.post(`/mailbox/threads/${thread.thread_id}/link-entreprise`, payload);
      setThread((prev) => prev ? { ...prev, crm_match: data.crm_match } : prev);
      setThreads((prev) => prev.map((x) => x.thread_id === thread.thread_id ? { ...x, crm_match: data.crm_match } : x));
      setLinkPopoverOpen(false);
      toast({ title: 'Email rattaché', description: data.crm_match.name });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  const unlinkCrm = async () => {
    if (!thread) return;
    try {
      await api.post(`/mailbox/threads/${thread.thread_id}/unlink-crm`);
      setThread((prev) => prev ? { ...prev, crm_match: null } : prev);
      setThreads((prev) => prev.map((x) => x.thread_id === thread.thread_id ? { ...x, crm_match: null } : x));
      toast({ title: 'Rattachement supprimé' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  // ----- Wave 1r — Keyboard shortcuts (Gmail-style)
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key;
      if (key === '?' || (key === '/' && e.shiftKey)) {
        e.preventDefault(); setShortcutsOpen(true); return;
      }
      if (key === '/') {
        e.preventDefault();
        document.querySelector('[data-testid="email-search-input"]')?.focus();
        return;
      }
      if (key === 'c') { e.preventDefault(); openCompose('new'); return; }
      if (key === 'r') {
        if (thread) { e.preventDefault(); openCompose('reply'); return; }
        else { e.preventDefault(); loadThreads(); return; }
      }
      if (key === 'a') { if (thread) { e.preventDefault(); openCompose('reply_all'); return; } }
      if (key === 'f') { if (thread) { e.preventDefault(); openCompose('forward'); return; } }
      if (key === 'j' || key === 'k') {
        e.preventDefault();
        if (threads.length === 0) return;
        const currIdx = threads.findIndex((t) => t.thread_id === selectedThreadId);
        const nextIdx = key === 'j'
          ? Math.min((currIdx === -1 ? -1 : currIdx) + 1, threads.length - 1)
          : Math.max(currIdx - 1, 0);
        const next = threads[nextIdx];
        if (next) openThread(next);
        return;
      }
      if (key === 'u') {
        if (thread) { e.preventDefault(); toggleReadUnread(); return; }
      }
      if (key === 'e') {
        if (thread) { e.preventDefault(); archive(); return; }
      }
      if (key === '#') {
        if (thread) { e.preventDefault(); moveToTrash(); return; }
      }
      if (key === 'Escape') {
        if (linkPopoverOpen) { setLinkPopoverOpen(false); return; }
        if (thread) { e.preventDefault(); setThread(null); setSelectedThreadId(null); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, threads, selectedThreadId, linkPopoverOpen]);

  const moveToTrash = async () => {
    if (!thread) return;
    try {
      await api.post(`/mailbox/threads/${thread.thread_id}/trash`, {
        message_ids: thread.messages.map((m) => m.message_id),
      });
      setThreads((prev) => prev.filter((x) => x.thread_id !== thread.thread_id));
      setThread(null);
      setSelectedThreadId(null);
      toast({ title: 'Déplacé vers la corbeille' });
      loadFolderCounts();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  // ----- Wave 1b helpers -----
  const openCompose = (mode = 'new', draftId = null) => {
    setComposeMode(mode);
    setComposeDraftId(draftId);
    setComposeOpen(true);
  };

  const onSent = () => {
    loadThreads();
    if (selectedThreadId) {
      api.get(`/mailbox/threads/${selectedThreadId}`).then(({ data }) => setThread(data)).catch(() => {});
    }
  };

  const openDraft = React.useCallback((draftId) => openCompose('draft', draftId), []);

  const deleteDraft = React.useCallback(async (draftId) => {
    setDrafts((p) => {
      const prev = p;
      api.delete(`/mailbox/drafts/${draftId}`)
        .then(() => { toast({ title: 'Brouillon supprimé' }); loadFolderCounts(); })
        .catch((err) => {
          setDrafts(prev);
          toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
        });
      return p.filter((d) => d.draft_id !== draftId);
    });
  }, [loadFolderCounts]);

  // Wave 1u4 — Quick "send to trash" from the thread row (hover icon).
  const quickTrashThread = React.useCallback(async (t) => {
    setThreads((p) => p.filter((x) => x.thread_id !== t.thread_id));
    setSelectedThreadId((sid) => {
      if (sid === t.thread_id) { setThread(null); return null; }
      return sid;
    });
    try {
      await api.post(`/mailbox/threads/${t.thread_id}/trash`, {
        message_ids: t.message_ids || [t.thread_id],
      });
      toast({ title: 'Déplacé vers la corbeille' });
      loadFolderCounts();
    } catch (err) {
      setThreads((p) => [t, ...p]);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  }, [loadFolderCounts]);

  const downloadAttachment = React.useCallback(async (msgId, att) => {
    try {
      const res = await api.get(`/mailbox/messages/${msgId}/attachments/${att.attachment_id}`, {
        params: { filename: att.filename, mime_type: att.mime_type },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: att.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'attachment'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Téléchargement impossible' });
    }
  }, []);

  const advFiltersCount = (advFilters.tags?.length || 0) + (advFilters.status ? 1 : 0) + (advFilters.assignee ? 1 : 0) + (advFilters.since ? 1 : 0) + (advFilters.until ? 1 : 0);
  const listThread = threads.find((x) => x.thread_id === selectedThreadId) || null;

  // ----------------------------- Render
  if (status && !status.has_inbox) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8 bg-[#FAFAFE]" data-testid="email-not-connected">
        <div className="soft-card p-6 max-w-md w-full">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
              <Mail className="w-6 h-6 text-[#4F46E5]" />
            </div>
            <div className="flex-1">
              <div className="text-[15px] font-bold text-[#1A1B2E]">Boîte email non connectée</div>
              <div className="text-[13px] text-[#6B6C85] mt-1">
                Pour voir vos emails directement dans OFManager, liez votre compte Google (Gmail) depuis votre profil. Outlook et IMAP arriveront prochainement.
              </div>
              <Link to="/app/profil" className="inline-flex items-center gap-1.5 h-9 px-4 mt-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold">
                Ouvrir le Profil <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex bg-white" data-testid="email-page">
      {/* ----------------------------- Sidebar */}
      <div style={{ width: `${sidebarWidth}px`, flex: '0 0 auto' }} className="h-full">
        <EmailSidebar
          accounts={accounts}
          selectedAccount={selectedAccount}
          onSelectAccount={(val) => {
            setSelectedAccount(val);
            loadThreads();
            loadLabels();
            loadFolderCounts();
          }}
          folder={folder}
          onSelectFolder={setFolder}
          labels={labels}
          statusEmail={status?.accounts?.[0]?.email}
          onCompose={() => openCompose('new')}
          folderCounts={folderCounts}
          onResetWidths={resetWidths}
          labelsCollapsed={labelsCollapsed}
          onToggleLabels={toggleLabels}
        />
      </div>
      <EmailResizeHandle
        active={resizing === 'sidebar'}
        onMouseDown={(e) => { e.preventDefault(); setResizing('sidebar'); }}
        onDoubleClick={resetWidths}
        testId="email-resize-sidebar"
      />

      {/* ----------------------------- Threads list */}
      <section style={{ width: `${threadsWidth}px`, flex: '0 0 auto' }} className="border-r border-[#EFF0F6] flex flex-col h-full">
        <EmailToolbar
          searchInput={searchInput}
          onChangeSearch={setSearchInput}
          onSubmitSearch={() => setSearch(searchInput.trim())}
          onRefresh={loadThreads}
          loadingList={loadingList}
          onSyncNow={syncNow}
          syncing={syncing}
          lastSyncLabel={lastSyncLabel}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onOpenSavedSearches={() => setSavedSearchModalOpen(true)}
          savedSearchesCount={savedSearches.length}
        />

        <div className="flex items-stretch border-b border-[#EFF0F6]">
          <div className="flex-1 min-w-0">
            <EmailQuickFilters
              threads={threads}
              quickFilter={quickFilter}
              onChange={setQuickFilter}
              focusMode={focusMode}
              onToggleFocus={() => setFocusMode((v) => !v)}
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            data-testid="email-advanced-filters-btn"
            title="Filtres avancés (tags, statut, assigné, dates)"
            className={`px-3 my-1 mx-2 rounded-lg text-[11.5px] font-semibold flex items-center gap-1.5 border ${advFiltersCount ? 'bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]' : 'bg-white border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE]'}`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filtres
            {advFiltersCount ? (
              <span className="ml-1 px-1.5 py-0 rounded-full bg-[#4F46E5] text-white text-[9px] font-bold">{advFiltersCount}</span>
            ) : null}
          </button>
        </div>

        <EmailBulkBar
          threads={threads}
          folder={folder}
          selectedThreadIds={selectedThreadIds}
          nextPageToken={nextPageToken}
          onSelectAll={() => setSelectedThreadIds(new Set(threads.map((t) => t.thread_id)))}
          onClearSelection={clearSelection}
          onBulk={runBulk}
        />

        <div className="flex-1 overflow-y-auto">
          <EmailThreadList
            loadingList={loadingList}
            folder={folder}
            drafts={drafts}
            visibleThreads={visibleThreads}
            totalThreads={threads.length}
            selectedThreadId={selectedThreadId}
            selectedThreadIds={selectedThreadIds}
            selectedAccount={selectedAccount}
            nextPageToken={nextPageToken}
            loadingMore={loadingMore}
            onOpenThread={openThread}
            onToggleSelect={toggleThreadSelected}
            onToggleStar={toggleStarOnThread}
            onQuickTrash={quickTrashThread}
            onOpenDraft={openDraft}
            onDeleteDraft={deleteDraft}
            onLoadMore={loadMoreThreads}
          />
        </div>
      </section>

      <EmailResizeHandle
        active={resizing === 'threads'}
        onMouseDown={(e) => { e.preventDefault(); setResizing('threads'); }}
        onDoubleClick={resetWidths}
        testId="email-resize-threads"
      />

      {/* ----------------------------- Thread viewer */}
      <section className="flex-1 min-w-0 flex flex-col bg-white h-full" data-testid="email-viewer">
        {!selectedThreadId ? (
          <div className="flex-1 flex items-center justify-center text-[#8E8FA5] text-[13px]">
            Sélectionnez un message à gauche pour le lire.
          </div>
        ) : loadingThread ? (
          <div className="flex-1 flex items-center justify-center text-[#8E8FA5] text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement du message…
          </div>
        ) : !thread ? (
          <div className="flex-1 flex items-center justify-center text-[#8E8FA5] text-[13px]">Erreur de chargement.</div>
        ) : (
          <>
            <EmailViewerHeader
              thread={thread}
              listThread={listThread}
              isUnread={!!listThread?.unread}
              onUnlinkCrm={unlinkCrm}
              onOpenLinkPopover={() => { setLinkPopoverOpen(true); fetchLinkSuggestions(); }}
              onTagsChange={(newTags) => {
                setThread((prev) => prev ? { ...prev, tags: newTags } : prev);
                setThreads((prev) => prev.map((x) => x.thread_id === thread.thread_id ? { ...x, tags: newTags } : x));
              }}
              onOpenCompose={openCompose}
              onToggleRead={toggleReadUnread}
              onArchive={archive}
              onTrash={moveToTrash}
              onClose={() => { setThread(null); setSelectedThreadId(null); }}
              onSnoozeOpen={() => setSnoozeOpen(true)}
            />
            <EmailMessageList messages={thread.messages} onDownloadAttachment={downloadAttachment} />
            {/* Wave 1u3 Phase C — internal collab panel */}
            <EmailThreadCollab
              threadId={thread.thread_id}
              onChange={(summary) => {
                setThreads((prev) => prev.map((x) => (
                  x.thread_id === thread.thread_id ? { ...x, ...summary } : x
                )));
              }}
            />
          </>
        )}
      </section>

      {/* Compose / Reply / Forward dialog */}
      <EmailComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={onSent}
        mode={composeMode}
        originalThread={thread}
        draftId={composeDraftId}
        accounts={accounts}
        currentAccount={selectedAccount}
      />
      <EmailSnoozePopover
        open={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
        onSnooze={snoozeThread}
        currentSnoozedUntil={thread?.snoozed_until}
        onUnsnooze={unsnoozeThread}
      />
      <EmailAdvancedFiltersPopover
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={advFilters}
        onApply={setAdvFilters}
        team={team}
      />
      <EmailLinkCrmModal
        open={linkPopoverOpen}
        onClose={() => setLinkPopoverOpen(false)}
        counterpartyEmail={thread?.counterparty_email}
        loading={linkLoading}
        suggestions={linkSuggestions}
        onLinkEntreprise={linkToEntreprise}
      />
      <EmailSavedSearchesModal
        open={savedSearchModalOpen}
        onClose={() => setSavedSearchModalOpen(false)}
        savedSearches={savedSearches}
        currentSearch={search}
        newSavedSearchName={newSavedSearchName}
        onChangeName={setNewSavedSearchName}
        onApply={applySavedSearch}
        onDelete={deleteSavedSearch}
        onSave={saveCurrentSearch}
      />
      <EmailShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
